// A LinkedIn post on a calendar item: write it, then schedule it or post it now, as the company's LinkedIn page.
// Once it's out, its link and numbers show here. Until LinkedIn is connected for real, posting is a stand-in: posts
// are recorded in Courage and their numbers are simulated, and the panel says so.
import { useEffect, useState } from "react";
import { ErrorBox } from "../ui";
import { reach, POST_MAX, type PostOut } from "./reachApi";

export type PostDraft = { text: string; linkUrl: string; imageUrl: string };
export const emptyPost: PostDraft = { text: "", linkUrl: "", imageUrl: "" };
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const localNow = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

/** The composer alone: used on a new item before it exists, and inside the full panel. */
export function PostFields({ v, onChange, disabled }: { v: PostDraft; onChange: (v: PostDraft) => void; disabled?: boolean }) {
  const left = POST_MAX - v.text.length;
  return (
    <>
      <div className="field" style={{ marginBottom: 6 }}>
        <label htmlFor="li-t">Post</label>
        <textarea id="li-t" className="textarea" rows={6} value={v.text} disabled={disabled} onChange={(e) => onChange({ ...v, text: e.target.value })}
          placeholder="What you'd say to a customer about it, in a few short lines. End with the one thing to do." style={{ height: "auto", padding: "6px 10px" }} />
        <span className={`rc-count${left < 0 ? " over" : ""}`}>{left < 0 ? `${-left} over LinkedIn's limit` : `${left.toLocaleString()} left`}</span>
      </div>
      <div className="form-grid">
        <div className="field"><label htmlFor="li-l">Link</label><input id="li-l" className="input" value={v.linkUrl} disabled={disabled} onChange={(e) => onChange({ ...v, linkUrl: e.target.value })} placeholder="https:// (a landing page, the article)" /></div>
        <div className="field"><label htmlFor="li-i">Picture</label><input id="li-i" className="input" value={v.imageUrl} disabled={disabled} onChange={(e) => onChange({ ...v, imageUrl: e.target.value })} placeholder="https:// (optional)" /></div>
      </div>
    </>
  );
}

/** The full panel for an item that exists: connection, composer, schedule or post now, and results. */
export function LinkedInPost({ content, publishDate, v, onDraft, onChanged }: {
  content: string; publishDate: string; v: PostDraft | null; onDraft: (v: PostDraft) => void; onChanged: () => void;
}) {
  const [out, setOut] = useState<PostOut | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [at, setAt] = useState("");
  const [picked, setPicked] = useState(false);
  const [connect, setConnect] = useState({ pageName: "", pageUrl: "" });
  useEffect(() => {
    setPicked(false);
    reach.post(content).then((o) => {
      setOut(o);
      onDraft({ text: o.post?.text ?? v?.text ?? "", linkUrl: o.post?.linkUrl ?? v?.linkUrl ?? "", imageUrl: o.post?.imageUrl ?? v?.imageUrl ?? "" });
    }, setErr);
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps
  // Until someone picks a time, the suggested one is 9 AM on the item's publish date (or tomorrow), never in the past.
  useEffect(() => {
    if (!out || picked) return;
    const base = out.post?.postAt ? new Date(out.post.postAt) : new Date(`${publishDate || localNow(new Date(Date.now() + 864e5)).slice(0, 10)}T09:00`);
    if (!out.post?.postAt && base.getTime() < Date.now() + 5 * 60_000) { base.setTime(Date.now() + 864e5); base.setHours(9, 0, 0, 0); }
    setAt(localNow(base));
  }, [out, publishDate, picked]);
  const post = out?.post ?? null;
  const locked = !!post && ["posting", "posted"].includes(post.status);
  const run = async (fn: () => Promise<PostOut>) => {
    setBusy(true); setErr(null);
    try { if (v && !locked) await reach.savePost(content, { text: v.text, linkUrl: v.linkUrl.trim() || null, imageUrl: v.imageUrl.trim() || null }); const o = await fn(); setOut(o); onChanged(); }
    catch (e) { setErr(e); }
    setBusy(false);
  };
  const doConnect = async () => { setBusy(true); setErr(null); try { const s = await reach.connectLinkedIn({ pageName: connect.pageName, pageUrl: connect.pageUrl.trim() || undefined }); setOut((o) => (o ? { ...o, linkedin: s.linkedin } : o)); } catch (e) { setErr(e); } setBusy(false); };
  if (!out) return <div className="rc-post"><ErrorBox error={err} />{!err && <span className="muted">Loading the post…</span>}</div>;
  return (
    <div className="rc-post" aria-label="LinkedIn post">
      <h3><span className="rc-li" aria-hidden="true">in</span>LinkedIn post</h3>
      {out.linkedin ? <p className="who">As {out.linkedin.pageName}{post?.status === "scheduled" && post.postAt ? ` · goes out ${when(post.postAt)}` : post?.status === "posted" && post.postedAt ? ` · posted ${when(post.postedAt)}` : ""}</p>
        : <p className="who">Connect your company's LinkedIn page to post from here.</p>}
      {out.mode === "test" && <p className="rc-test">Test mode: posts are recorded here and nothing goes to LinkedIn. Results are simulated.</p>}
      <ErrorBox error={err} />
      {post?.status === "failed" && post.error && <div className="msg error" role="alert">LinkedIn didn't take it: {post.error}</div>}
      {locked ? (
        <>
          <p style={{ whiteSpace: "pre-wrap", margin: "0 0 8px", fontSize: 14 }}>{post!.text}</p>
          {post!.postUrl && <a href={post!.postUrl} target="_blank" rel="noreferrer">View it on LinkedIn</a>}
          <div className="rc-stats" aria-label="Results">
            {([["impressions", "Impressions"], ["reactions", "Reactions"], ["comments", "Comments"], ["shares", "Reposts"], ["clicks", "Clicks"]] as const).map(([k, l]) =>
              <div key={k}><b>{(post![k] ?? 0).toLocaleString()}</b><span>{l}</span></div>)}
          </div>
          <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>{post!.statsAt ? `Updated ${when(post!.statsAt)}. Numbers refresh every hour for two weeks.` : "Numbers come in over the first hours."}</p>
        </>
      ) : (
        <>
          <PostFields v={v ?? { text: "", linkUrl: "", imageUrl: "" }} onChange={onDraft} disabled={busy} />
          {!out.linkedin ? (
            <div className="form-grid" style={{ alignItems: "end" }}>
              <div className="field"><label htmlFor="li-pn">Company page name</label><input id="li-pn" className="input" value={connect.pageName} onChange={(e) => setConnect({ ...connect, pageName: e.target.value })} placeholder="Acme Fabrication" /></div>
              <div className="field"><label htmlFor="li-pu">Page address</label><input id="li-pu" className="input" value={connect.pageUrl} onChange={(e) => setConnect({ ...connect, pageUrl: e.target.value })} placeholder="https://www.linkedin.com/company/…" /></div>
              <div><button type="button" className="btn" disabled={busy || !connect.pageName.trim()} onClick={doConnect}>Connect the page</button></div>
            </div>
          ) : post?.status === "scheduled" ? (
            <div className="rc-post-actions">
              <button type="button" className="btn" disabled={busy} onClick={() => run(() => reach.unschedulePost(content))}>Unschedule</button>
              <button type="button" className="btn" disabled={busy || !v?.text.trim()} onClick={() => { if (confirm("Post it on LinkedIn now?")) run(() => reach.postNow(content)); }}>Post now instead</button>
              <span className="muted" style={{ fontSize: 12 }}>Drag it to another day on the calendar to move it; the time stays.</span>
            </div>
          ) : (
            <div className="rc-post-actions">
              <input type="datetime-local" className="input" aria-label="When it goes out" value={at} onChange={(e) => { setAt(e.target.value); setPicked(true); }} />
              <button type="button" className="btn primary" disabled={busy || !v?.text.trim() || !at || (v?.text.length ?? 0) > POST_MAX} onClick={() => run(() => reach.schedulePost(content, new Date(at).toISOString()))}>Schedule</button>
              <button type="button" className="btn" disabled={busy || !v?.text.trim() || (v?.text.length ?? 0) > POST_MAX} onClick={() => { if (confirm("Post it on LinkedIn now?")) run(() => reach.postNow(content)); }}>Post now</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
